import { Directive, ElementRef, Input, AfterViewInit, OnDestroy } from '@angular/core';

@Directive({
  selector: '[appNumericInput]',
  standalone: false
})
export class NumericInputDirective implements AfterViewInit, OnDestroy {
  @Input() maxDigits = 10;
  @Input() allowDecimals = false;
  @Input() maxDecimalPlaces = 2;
  private inputElement: HTMLInputElement | null = null;
  private inputListener: ((event: Event) => void) | null = null;
  private pasteListener: ((event: ClipboardEvent) => void) | null = null;
  private isProcessing = false;

  constructor(private el: ElementRef) { }

  ngAfterViewInit() {
    setTimeout(() => {
      this.inputElement = this.el.nativeElement.querySelector('input');

      if (this.inputElement) {
        this.inputElement.setAttribute('inputmode', this.allowDecimals ? 'decimal' : 'numeric');
        this.attachListeners();
      }
    }, 100);
  }

  ngOnDestroy() {
    this.removeListeners();
  }

  private attachListeners() {
    if (!this.inputElement) {
      return;
    }

    this.inputListener = (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (!input || this.isProcessing) {
        return;
      }

      const sanitized = this.allowDecimals
        ? this.sanitizeDecimal(input.value)
        : input.value.replace(/\D/g, '').slice(0, this.maxDigits);

      if (input.value !== sanitized) {
        this.isProcessing = true;
        input.value = sanitized;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        this.isProcessing = false;
      }
    };

    this.pasteListener = (event: ClipboardEvent) => {
      event.preventDefault();
      const pastedText = event.clipboardData?.getData('text') || '';
      const sanitized = this.allowDecimals
        ? this.sanitizeDecimal(pastedText)
        : pastedText.replace(/\D/g, '').slice(0, this.maxDigits);

      const input = event.target as HTMLInputElement;
      if (input && sanitized) {
        input.value = sanitized;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };

    this.inputElement.addEventListener('input', this.inputListener);
    this.inputElement.addEventListener('paste', this.pasteListener);
  }

  private sanitizeDecimal(raw: string): string {
    const value = raw.replace(/[^\d.]/g, '');
    if (!value) {
      return '';
    }
    const parts = value.split('.');
    const intPart = parts[0].slice(0, this.maxDigits);
    const decPart = parts.length > 1 ? '.' + parts.slice(1).join('').slice(0, this.maxDecimalPlaces) : '';
    return (intPart || (decPart ? '0' : '')) + decPart;
  }

  private removeListeners() {
    if (this.inputElement && this.inputListener) {
      this.inputElement.removeEventListener('input', this.inputListener);
    }
    if (this.inputElement && this.pasteListener) {
      this.inputElement.removeEventListener('paste', this.pasteListener);
    }
  }
}
